import React, { useState, useEffect } from "react";
import axios from "axios";
import { SERVER_URL } from "../../constants";
import { useUser, useClerk } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function PersonalInfo({ account }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const userId = user.id;

  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [otpStep, setOtpStep] = useState(false); // Controls OTP modal visibility
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [originalPhone, setOriginalPhone] = useState(""); // Track original phone number

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    img_url: "",
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/user/${userId}`);
        if (response.data.success && response.data.data) {
          const userData = response.data.data;
          setFormData({
            firstName: userData.firstName || user?.firstName || "",
            lastName: userData.lastName || user?.lastName || "",
            phone: userData.phone || "",
            img_url: userData.img_url || "",
          });
          setOriginalPhone(userData.phone || "");
          setIsNewUser(false);
        } else {
          setFormData({
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            phone: "",
            img_url: "",
          });
          setIsNewUser(true);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setFormData({
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            phone: "",
            img_url: "",
          });
          setIsNewUser(true);
        } else {
          setError("Error loading user data. Please refresh the page.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId, user]);

  useEffect(() => {
    if (account) {
      setFormData((prev) => ({
        ...prev,
        firstName: account.firstName || prev.firstName,
        lastName: account.lastName || prev.lastName,
        phone: account.phone || prev.phone,
        img_url: account.img_url || prev.img_url,
      }));
      setOriginalPhone(account.phone || "");
      setIsNewUser(false);
    }
  }, [account]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setError(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG)");
      return;
    }

    const imageData = new FormData();
    imageData.append("file", file);
    imageData.append("upload_preset", "unsigned_preset");

    setLoading(true);
    try {
      const response = await axios.post(
        "https://api.cloudinary.com/v1_1/dkzwhqhbr/image/upload",
        imageData
      );
      setFormData((prev) => ({
        ...prev,
        img_url: response.data.secure_url,
      }));
      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/v1/send-otp`, {
        phone: `+91${formData.phone}`, // Assuming Indian numbers for this example
      });
      if (response.data.success) {
        setOtpSent(true);
        toast.success("OTP sent successfully!");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp) {
      toast.error("Please enter the OTP.");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/v1/verify-otp`, {
        phone: `+91${formData.phone}`,
        otp,
      });
      if (response.data.success) {
        await handleProfileUpdate();
        toast.success("Phone number verified and profile updated!");
        setOtpStep(false);
        setOtpSent(false);
        setOtp("");
        setOriginalPhone(formData.phone);
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    const payload = { userId, ...formData };
    const response = await axios({
      method: isNewUser ? "post" : "put",
      url: isNewUser
        ? `${SERVER_URL}/api/v1/user`
        : `${SERVER_URL}/api/v1/user/${userId}`,
      data: payload,
    });

    if (response.data.success) {
      const updatedUser = response.data.data;
      setFormData({
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        phone: updatedUser.phone,
        img_url: updatedUser.img_url,
      });
      setIsNewUser(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    setError(null);

    try {
      if (!/^\d{10}$/.test(formData.phone)) {
        throw new Error("Phone number must be exactly 10 digits");
      }

      if (formData.phone !== originalPhone && !isNewUser) {
        setOtpStep(true); // Trigger OTP verification if phone number changed
      } else {
        await handleProfileUpdate();
        toast.success(
          isNewUser ? "Profile created successfully!" : "Profile updated successfully!"
        );
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      setError(error.message || "Error saving profile. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <ClipLoader size={40} color={"#2563eb"} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 sm:mb-8">
        {isNewUser ? "Create Your Profile" : "Update Your Profile"}
      </h2>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg shadow-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload Section */}
        <div className="mb-8">
          <label className="block text-gray-700 font-medium mb-3">Profile Photo</label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center bg-white hover:border-teal-500 transition-all duration-300">
            {formData.img_url ? (
              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <img
                  src={formData.img_url}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover shadow-md"
                />
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageUpload}
                  className="w-full sm:w-auto text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition-all duration-200"
                  disabled={saveLoading}
                />
              </div>
            ) : (
              <div className="relative">
                <div className="flex justify-center mb-4">
                  <svg
                    className="w-16 h-16 text-teal-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-gray-700 font-medium">Drop your image here</p>
                  <p className="text-gray-500 text-sm mt-1">
                    (Only *.jpg and *.png, max 5MB)
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageUpload}
                  className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                  disabled={saveLoading}
                />
              </div>
            )}
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 font-medium mb-2">First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Enter your first name"
              required
              disabled={saveLoading}
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Enter your last name"
              required
              disabled={saveLoading}
            />
          </div>

          <div className="col-span-1 sm:col-span-2">
            <label className="block text-gray-700 font-medium mb-2">Phone/Mobile</label>
            <div className="flex">
              <span className="inline-flex items-center px-4 py-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-100 text-gray-700 font-medium">
                +91
              </span>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white shadow-sm"
                placeholder="Enter 10-digit phone number"
                maxLength="10"
                required
                disabled={saveLoading}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-8">
          <button
            type="submit"
            disabled={saveLoading || loading}
            className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
          >
            {saveLoading ? (
              <ClipLoader size={20} color={"#ffffff"} className="mr-2" />
            ) : null}
            {saveLoading ? "Saving..." : isNewUser ? "Create Profile" : "Update Profile"}
          </button>
        </div>
      </form>

      {/* OTP Verification Modal */}
      {otpStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Verify Your Phone Number
            </h3>
            <p className="text-gray-600 mb-6">
              We’ve sent an OTP to +91{formData.phone}. Please enter it below to verify.
            </p>

            {!otpSent ? (
              <button
                onClick={sendOtp}
                disabled={otpLoading}
                className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {otpLoading ? (
                  <ClipLoader size={20} color={"#ffffff"} className="mr-2" />
                ) : null}
                {otpLoading ? "Sending..." : "Send OTP"}
              </button>
            ) : (
              <>
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white shadow-sm text-center text-xl tracking-widest"
                    placeholder="••••••"
                  />
                </div>
                <button
                  onClick={verifyOtp}
                  disabled={otpLoading || otp.length < 6}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {otpLoading ? (
                    <ClipLoader size={20} color={"#ffffff"} className="mr-2" />
                  ) : null}
                  {otpLoading ? "Verifying..." : "Verify OTP"}
                </button>
              </>
            )}

            <button
              onClick={() => {
                setOtpStep(false);
                setOtpSent(false);
                setOtp("");
                setFormData((prev) => ({ ...prev, phone: originalPhone }));
              }}
              className="w-full mt-4 text-gray-600 hover:text-gray-800 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mobile Logout Button */}
      <div className="sm:hidden flex justify-end mt-6">
        <button
          onClick={() => signOut(() => navigate("/"))}
          className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-all duration-300 shadow-md hover:shadow-lg"
        >
          Log Out
        </button>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
